import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/layout/AppLayout';

// Existing pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import StudentsModule from './pages/students/StudentsModule';
import StaffModule from './pages/staff/StaffModule';
import ClassesManagement from './pages/classes/ClassesManagement';
import DepartmentsManagement from './pages/departments/DepartmentsManagement';
import AcademicManagement from './pages/academic/AcademicManagement';
import ExaminationManagement from './pages/examination/ExaminationManagement';
import FeeManagement from './pages/fees/FeeManagement';
import AttendanceManagement from './pages/attendance/AttendanceManagement';
import CommunicationModule from './pages/communication/CommunicationModule';
import TransportManagement from './pages/transport/TransportManagement';
import LibraryManagement from './pages/library/LibraryManagement';
import HostelManagement from './pages/hostel/HostelManagement';
import ReportsAnalytics from './pages/reports/ReportsAnalytics';
import Administration from './pages/admin/Administration';

// New Modules
import TicketManagement from './pages/tickets/TicketManagement';
import InventoryManagement from './pages/inventory/InventoryManagement';
import InquiryManagement from './pages/inquiry/InquiryManagement';
import HRManagement from './pages/hr/HRManagement';
import GradeManagement from './pages/grades/GradeManagement';
import BranchManagement from './pages/branches/BranchManagement';
import IdCardConfig from './pages/idCardConfiguration/IdCardConfig';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          color: "#64748b",
        }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span>Loading School ERP...</span>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>

      {/* Default URL */}
      <Route
        path="/"
        element={
          isAuthenticated
            ? <Navigate to="/dashboard" replace />
            : <Navigate to="/login" replace />
        }
      />

      {/* Authentication */}
      <Route
        path="/login"
        element={
          isAuthenticated
            ? <Navigate to="/dashboard" replace />
            : <Login />
        }
      />

      <Route
        path="/signup"
        element={
          isAuthenticated
            ? <Navigate to="/dashboard" replace />
            : <Signup />
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="students" element={<StudentsModule />} />
        <Route path="staff" element={<StaffModule />} />
        <Route path="classes" element={<ClassesManagement />} />
        <Route path="departments" element={<DepartmentsManagement />} />
        <Route path="academic" element={<AcademicManagement />} />
        <Route path="examination" element={<ExaminationManagement />} />
        <Route path="id-cards-config" element={<IdCardConfig />} />
        <Route path="fees" element={<FeeManagement />} />
        <Route path="attendance" element={<AttendanceManagement />} />
        <Route path="communication" element={<CommunicationModule />} />
        <Route path="transport" element={<TransportManagement />} />
        <Route path="library" element={<LibraryManagement />} />
        <Route path="hostel" element={<HostelManagement />} />
        <Route path="reports" element={<ReportsAnalytics />} />
        <Route path="admin" element={<Administration />} />
        <Route path="tickets" element={<TicketManagement />} />
        <Route path="inventory" element={<InventoryManagement />} />
        <Route path="inquiry" element={<InquiryManagement />} />
        <Route path="hr" element={<HRManagement />} />
        <Route path="grades" element={<GradeManagement />} />
        <Route path="branches" element={<BranchManagement />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/login" replace />} />

    </Routes>
  );
};

const App = () => {
  return (
    <BrowserRouter basename="/schoolerp">
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 500,
            },
            success: {
              style: {
                background: "#f0fdf4",
                color: "#15803d",
                border: "1px solid #86efac",
              },
            },
            error: {
              style: {
                background: "#fef2f2",
                color: "#b91c1c",
                border: "1px solid #fca5a5",
              },
            },
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;